/**
 * ManageVenues page for venue owners.
 * Fetches real venues via trpc.venue.getMyVenues.
 * Fetches real pending/accepted/rejected events via trpc.event.getEventsByVenueId.
 * Accept → 'Accepted', Decline → 'Rejected', Revert → back to 'Pending'.
 * If accepting would cause a time conflict, the Accept button is greyed out
 * and a warning is shown inline when the row is expanded — no need to click Accept first.
 *
 * Statistics tab — aggregate venue stats via VenueStatsTab component.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { trpc } from '../utils/trpc';
import VenueProfilePanel from '../components/VenueProfilePanel';
import VenueStatsTab from './VenueStatsTab';
import styles from '../styles/ManageVenues.module.css';
import { supabase } from '../utils/supabase';

// ─── Types ───────────────────────────────────────────────────

interface VenueRow {
  venue_id: string;
  venue_name: string;
  address: string | null;
  open_time: string | null;
  close_time: string | null;
  max_capacity: number | null;
  tags: string[] | null;
}

interface EventRow {
  event_id: string;
  event_name: string;
  date: string;
  duration_minutes: number | null;
  max_capacity: number | null;
  description: string | null;
  group_id: string | null;
  venue_id: string | null;
  status: 'Pending' | 'Accepted' | 'Rejected' | null;
}

function initials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');
}

// ─── Time helpers ─────────────────────────────────────────────

const DEFAULT_DURATION_MIN = 120;

function fmt12(date: Date): string {
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function fmtDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function getEndTime(event: EventRow): Date {
  const start = new Date(event.date);
  const durationMs = (event.duration_minutes ?? DEFAULT_DURATION_MIN) * 60 * 1000;
  return new Date(start.getTime() + durationMs);
}

function findConflict(pending: EventRow, acceptedEvents: EventRow[]): EventRow | null {
  const proposedStart = new Date(pending.date);
  const proposedEnd = getEndTime(pending);

  return (
    acceptedEvents.find((existing) => {
      const existingStart = new Date(existing.date);
      const existingEnd = getEndTime(existing);
      return proposedStart < existingEnd && proposedEnd > existingStart;
    }) ?? null
  );
}

// ─── RequestRow ──────────────────────────────────────────────

function RequestRow({
  event,
  acceptedEvents,
  onAccept,
  onDecline,
  expanded,
  onToggle,
}: {
  event: EventRow;
  acceptedEvents: EventRow[];
  onAccept: (id: string) => void;
  onDecline: (id: string) => void;
  expanded: boolean;
  onToggle: (id: string) => void;
}) {
  const start = new Date(event.date);
  const end = getEndTime(event);
  const conflict = findConflict(event, acceptedEvents);

  return (
    <div
      className={`${styles.requestRow} ${expanded ? styles.requestRowExpanded : ''} ${conflict ? styles.requestRowConflict : ''}`}
    >
      <div className={styles.requestRowTop} onClick={() => onToggle(event.event_id)}>
        <div className={styles.requestRowLeft}>
          <span className={styles.requestTitle}>{event.event_name}</span>
        </div>
        <div className={styles.requestMeta}>
          <span className={styles.metaChip}>{fmtDate(start)}</span>
          <span className={styles.metaDot} />
          <span className={styles.metaChip}>
            {fmt12(start)} – {fmt12(end)}
          </span>
          {event.max_capacity && (
            <>
              <span className={styles.metaDot} />
              <span className={styles.metaChip}>{event.max_capacity} capacity</span>
            </>
          )}
          {conflict && <span className={styles.conflictPill}>⚠ Conflict</span>}
        </div>
        <div className={`${styles.chevron} ${expanded ? styles.chevronOpen : ''}`}>›</div>
      </div>

      {expanded && (
        <div className={styles.requestExpanded}>
          <div className={styles.timeBlock}>
            <div className={styles.timeBlockItem}>
              <span className={styles.timeBlockLabel}>Start</span>
              <span className={styles.timeBlockValue}>
                {fmtDate(start)} · {fmt12(start)}
              </span>
            </div>
            <div className={styles.timeBlockDivider} />
            <div className={styles.timeBlockItem}>
              <span className={styles.timeBlockLabel}>End</span>
              <span className={styles.timeBlockValue}>
                {fmtDate(end)} · {fmt12(end)}
              </span>
            </div>
            <div className={styles.timeBlockDivider} />
            <div className={styles.timeBlockItem}>
              <span className={styles.timeBlockLabel}>Duration</span>
              <span className={styles.timeBlockValue}>
                {event.duration_minutes ?? DEFAULT_DURATION_MIN} min
              </span>
            </div>
          </div>

          {conflict && (
            <div className={styles.inlineConflict}>
              <span className={styles.inlineConflictIcon}>⚠️</span>
              <p className={styles.inlineConflictText}>
                Conflicts with <strong>{conflict.event_name}</strong> (
                {fmt12(new Date(conflict.date))} – {fmt12(getEndTime(conflict))}). Accept
                is disabled until the conflict is resolved.
              </p>
            </div>
          )}

          {event.description && (
            <p className={styles.requestDescription}>{event.description}</p>
          )}

          <div className={styles.requestActions}>
            <button
              className={styles.btnAccept}
              onClick={() => onAccept(event.event_id)}
              disabled={!!conflict}
              title={conflict ? `Conflicts with "${conflict.event_name}"` : undefined}
            >
              Accept
            </button>
            <button
              className={styles.btnDecline}
              onClick={() => onDecline(event.event_id)}
            >
              Decline
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── VenuePanel ──────────────────────────────────────────────

function VenuePanel({ venue }: { venue: VenueRow }) {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const [activeTab, setActiveTab] = useState<'pending' | 'events' | 'rejected' | 'stats'>(
    'pending',
  );
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [conflictError, setConflictError] = useState<string | null>(null);

  const { data: pendingEvents = [], isLoading: pendingLoading } =
    trpc.event.getEventsByVenueId.useQuery({
      venueId: venue.venue_id,
      status: 'Pending',
      upcomingOnly: false,
    });

  const { data: acceptedEvents = [], isLoading: acceptedLoading } =
    trpc.event.getEventsByVenueId.useQuery({
      venueId: venue.venue_id,
      status: 'Accepted',
      upcomingOnly: false,
    });

  const { data: rejectedEvents = [], isLoading: rejectedLoading } =
    trpc.event.getEventsByVenueId.useQuery({
      venueId: venue.venue_id,
      status: 'Rejected',
      upcomingOnly: false,
    });

  const acceptMutation = trpc.event.acceptEventRequest.useMutation({
    onSuccess: () => {
      utils.event.getEventsByVenueId.invalidate();
      setExpandedId(null);
      setConflictError(null);
    },
    onError: (err) => {
      if (err.data?.code === 'CONFLICT') {
        setConflictError(err.message);
      } else {
        console.error('Accept failed:', err.message);
      }
    },
  });

  const declineMutation = trpc.event.declineEventRequest.useMutation({
    onSuccess: () => {
      utils.event.getEventsByVenueId.invalidate();
      setExpandedId(null);
    },
    onError: (err) => {
      console.error('Decline failed:', err.message);
    },
  });

  const revertMutation = trpc.event.revertEventRequest.useMutation({
    onSuccess: () => {
      utils.event.getEventsByVenueId.invalidate();
    },
    onError: (err) => {
      console.error('Revert failed:', err.message);
    },
  });

  const handleAccept = (id: string) => {
    setConflictError(null);
    acceptMutation.mutate({ eventId: id });
  };
  const handleDecline = (id: string) => declineMutation.mutate({ eventId: id });
  const handleRevert = (id: string) => revertMutation.mutate({ eventId: id });
  const handleToggle = (id: string) => setExpandedId((prev) => (prev === id ? null : id));

  return (
    <>
      {/* Venue KPI strip */}
      <div className={styles.venueHeader}>
        <div className={styles.venueHeaderInfo}>
          <h2 className={styles.venueHeaderName}>{venue.venue_name}</h2>
          <p className={styles.venueHeaderAddress}>{venue.address ?? 'No address set'}</p>
          {venue.tags && venue.tags.length > 0 && (
            <div className={styles.venueHeaderTags}>
              {venue.tags.map((t: string) => (
                <span key={t} className={styles.venueHeaderTag}>
                  {t}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className={styles.venueKpis}>
          <div className={styles.kpi}>
            <span className={styles.kpiLabel}>Hours</span>
            <span className={styles.kpiValue}>
              {venue.open_time && venue.close_time
                ? `${venue.open_time}–${venue.close_time}`
                : '—'}
            </span>
          </div>
          <div className={styles.kpi}>
            <span className={styles.kpiLabel}>Capacity</span>
            <span className={styles.kpiValue}>{venue.max_capacity ?? '—'}</span>
          </div>
          <div className={styles.kpi}>
            <span className={styles.kpiLabel}>Pending</span>
            <span
              className={`${styles.kpiValue} ${pendingEvents.length > 0 ? styles.kpiValuePending : ''}`}
            >
              {pendingLoading ? '…' : pendingEvents.length}
            </span>
          </div>
          <div className={styles.kpi}>
            <span className={styles.kpiLabel}>Accepted</span>
            <span className={styles.kpiValue}>
              {acceptedLoading ? '…' : acceptedEvents.length}
            </span>
          </div>
          <div className={styles.kpi}>
            <span className={styles.kpiLabel}>Rejected</span>
            <span className={styles.kpiValue}>
              {rejectedLoading ? '…' : rejectedEvents.length}
            </span>
          </div>
        </div>

        <div className={styles.venueHeaderActions}>
          <button
            className={styles.btnEditVenue}
            onClick={() => navigate(`/venues/edit?venueId=${venue.venue_id}`)}
          >
            Edit Venue
          </button>
        </div>
      </div>

      {/* Requests + always-visible profile panel */}
      <div className={styles.contentRow}>
        <div className={styles.panel}>
          {/* Shimmer stripe */}
          <div className={styles.stripe}>
            <div className="gsShimmerBar" />
          </div>

          {/* Fallback conflict banner */}
          {conflictError && (
            <div className={styles.conflictBanner}>
              <span className={styles.conflictIcon}>⚠️</span>
              <p className={styles.conflictText}>{conflictError}</p>
              <button
                className={styles.conflictDismiss}
                onClick={() => setConflictError(null)}
                aria-label="Dismiss conflict warning"
              >
                ✕
              </button>
            </div>
          )}

          <div className={styles.panelInner}>
            <div className={styles.tabRow}>
              <button
                className={`${styles.tab} ${activeTab === 'pending' ? styles.tabActive : ''}`}
                onClick={() => setActiveTab('pending')}
              >
                Pending Requests
                {pendingEvents.length > 0 && (
                  <span className={styles.tabBadgeOrange}>{pendingEvents.length}</span>
                )}
              </button>
              <button
                className={`${styles.tab} ${activeTab === 'events' ? styles.tabActive : ''}`}
                onClick={() => setActiveTab('events')}
              >
                Accepted Events
                {acceptedEvents.length > 0 && (
                  <span className={styles.tabBadgeGreen}>{acceptedEvents.length}</span>
                )}
              </button>
              <button
                className={`${styles.tab} ${activeTab === 'rejected' ? styles.tabActive : ''}`}
                onClick={() => setActiveTab('rejected')}
              >
                Rejected
                {rejectedEvents.length > 0 && (
                  <span className={styles.tabBadgeOrange}>{rejectedEvents.length}</span>
                )}
              </button>
              <button
                className={`${styles.tab} ${activeTab === 'stats' ? styles.tabActive : ''}`}
                onClick={() => setActiveTab('stats')}
              >
                Statistics
              </button>
            </div>

            <div className={styles.tabContent}>
              {/* Pending Tab */}
              {activeTab === 'pending' && (
                <>
                  {pendingLoading && <div className={styles.loadingSpinner} />}
                  {!pendingLoading && pendingEvents.length === 0 && (
                    <p className={styles.emptyState}>
                      No pending requests — all caught up.
                    </p>
                  )}
                  {(pendingEvents as EventRow[]).map((event) => (
                    <RequestRow
                      key={event.event_id}
                      event={event}
                      acceptedEvents={acceptedEvents as EventRow[]}
                      onAccept={handleAccept}
                      onDecline={handleDecline}
                      expanded={expandedId === event.event_id}
                      onToggle={handleToggle}
                    />
                  ))}
                </>
              )}

              {/* Accepted Tab */}
              {activeTab === 'events' && (
                <>
                  {acceptedLoading && <div className={styles.loadingSpinner} />}
                  {!acceptedLoading && acceptedEvents.length === 0 && (
                    <p className={styles.emptyState}>No accepted events yet.</p>
                  )}
                  {(acceptedEvents as EventRow[]).map((event) => {
                    const start = new Date(event.date);
                    const end = getEndTime(event);
                    return (
                      <div key={event.event_id} className={styles.acceptedRow}>
                        <div className={styles.requestRowLeft}>
                          <span className={styles.requestTitle}>{event.event_name}</span>
                        </div>
                        <div className={styles.requestMeta}>
                          <span className={styles.metaChip}>{fmtDate(start)}</span>
                          <span className={styles.metaDot} />
                          <span className={styles.metaChip}>
                            {fmt12(start)} – {fmt12(end)}
                          </span>
                          {event.max_capacity && (
                            <>
                              <span className={styles.metaDot} />
                              <span className={styles.metaChip}>
                                {event.max_capacity} capacity
                              </span>
                            </>
                          )}
                        </div>
                        <button
                          className={styles.btnAccept}
                          onClick={() => handleRevert(event.event_id)}
                        >
                          Move to Pending
                        </button>
                      </div>
                    );
                  })}
                </>
              )}

              {/* Rejected Tab */}
              {activeTab === 'rejected' && (
                <>
                  {rejectedLoading && <div className={styles.loadingSpinner} />}
                  {!rejectedLoading && rejectedEvents.length === 0 && (
                    <p className={styles.emptyState}>No rejected events.</p>
                  )}
                  {(rejectedEvents as EventRow[]).map((event) => {
                    const start = new Date(event.date);
                    const end = getEndTime(event);
                    return (
                      <div key={event.event_id} className={styles.acceptedRow}>
                        <div className={styles.requestRowLeft}>
                          <span className={styles.requestTitle}>{event.event_name}</span>
                        </div>
                        <div className={styles.requestMeta}>
                          <span className={styles.metaChip}>{fmtDate(start)}</span>
                          <span className={styles.metaDot} />
                          <span className={styles.metaChip}>
                            {fmt12(start)} – {fmt12(end)}
                          </span>
                          {event.max_capacity && (
                            <>
                              <span className={styles.metaDot} />
                              <span className={styles.metaChip}>
                                {event.max_capacity} capacity
                              </span>
                            </>
                          )}
                        </div>
                        <button
                          className={styles.btnAccept}
                          onClick={() => handleRevert(event.event_id)}
                        >
                          Move to Pending
                        </button>
                      </div>
                    );
                  })}
                </>
              )}

              {/* Statistics Tab */}
              {activeTab === 'stats' && <VenueStatsTab venueId={venue.venue_id} />}
            </div>
          </div>
        </div>
        <VenueProfilePanel venueName={venue.venue_name} />
      </div>
    </>
  );
}

// ─── Page ────────────────────────────────────────────────────

function ManageVenues() {
  const navigate = useNavigate();
  const { data: venues, isLoading, error } = trpc.venue.getMyVenues.useQuery();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const selectedVenue = venues?.[selectedIndex] ?? null;

  return (
    <div className={styles.shell}>
      <div className={styles.topBar}>
        <button className={styles.topBarLogoBtn} onClick={() => navigate('/dashboard')}>
          <img
            onLoad={(e) => e.currentTarget.classList.add('loaded')}
            src="/GroupSoup.png"
            alt="GroupSoup"
            className={styles.topBarLogo}
          />
        </button>
        <div className={styles.topBarDivider} />
        <span className={styles.topBarTitle}>Venue Management</span>
        <span className={styles.topBarSubtitle}>
          {venues ? `${venues.length} venue${venues.length !== 1 ? 's' : ''}` : ''}
        </span>
        <div className={styles.topBarActions}>
          <button
            className={styles.btnTopCreate}
            onClick={() => navigate('/venues/create')}
          >
            + New Venue
          </button>
          <button className={styles.btnTopBack} onClick={() => handleSignOut()}>
            Sign Out
          </button>
        </div>
      </div>

      <div className={styles.body}>
        <nav className={styles.sidebar}>
          <span className={styles.sidebarLabel}>Your Venues</span>
          {isLoading && <div className={styles.loadingSpinner} />}
          {venues?.map((venue: VenueRow, i: number) => (
            <button
              key={venue.venue_id}
              className={`${styles.sidebarVenueBtn} ${i === selectedIndex ? styles.sidebarVenueBtnActive : ''}`}
              onClick={() => setSelectedIndex(i)}
            >
              <div className={styles.sidebarVenueIcon}>{initials(venue.venue_name)}</div>
              <div className={styles.sidebarVenueInfo}>
                <span className={styles.sidebarVenueName}>{venue.venue_name}</span>
                <span className={styles.sidebarVenueMeta}>
                  {venue.max_capacity ? `Cap. ${venue.max_capacity}` : 'No capacity set'}
                </span>
              </div>
            </button>
          ))}
        </nav>

        <main className={styles.main}>
          {isLoading && <div className={styles.loadingSpinner} />}
          {error && (
            <p className={styles.errorText}>Failed to load venues: {error.message}</p>
          )}
          {!isLoading && !error && venues?.length === 0 && (
            <p className={styles.emptyState}>
              No venues yet — create one to get started.
            </p>
          )}
          {selectedVenue && (
            <VenuePanel key={selectedVenue.venue_id} venue={selectedVenue} />
          )}
        </main>
      </div>
    </div>
  );
}

export default ManageVenues;
